import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Popover, useTheme } from '@mui/material';
import { PreviewType } from 'app/pages/system/forms/FormPreview/FormPreview.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { SignatureDynamicValueTypes } from 'constants/dynamicValues';
import {
  FormBuilderMethodType,
  FormBuilderOperationType,
  PopupDynamicFieldView,
  ReactRenderingContent,
} from 'constants/formBuilder';
import { CheckboxRenderType, FormSchemaType } from 'constants/forms';
import { floatOptions } from 'constants/options';
import { defaultPopoverOptions } from 'constants/popover';
import usePopover from 'hooks/usePopover';
import { getImageDimensions } from 'utils/file';
import {
  checkIsTagAssignedDate,
  checkIsTagName,
  flatDynamicFields,
  getDynamicFieldCatalogName,
  getDynamicFieldDisplayNameWithPath,
  isPdfFormType,
} from 'utils/forms';

import { selectDefaultPosConfigurationLogo } from 'store/companies/selectors';
import { selectDuplicateDynamicFieldNames } from 'store/forms/selectors';
import { closePopupAction, openPopupAction } from 'store/global/actions';

import { formsTestIds } from '../../forms.testIds';
import { getStyles } from '../FormEditor.styles';

import DynamicFieldEditDeleteOptionsView from './DynamicFieldEditDeleteOptionsView';
import DynamicFieldHandWritingSignatureOptions from './DynamicFieldHandWritingSignatureOptions';
import DynamicFieldItemsList from './DynamicFieldItemsList';
import DynamicFieldNotaryOptionsView from './DynamicFieldNotaryOptionsView';
import { getPreviewVerticalAlign } from './DynamicFieldOptions.utils';
import RateCurrencyOptions from './DynamicFieldRateCurrencyOptions';
import DynamicFieldStyleOptionsView from './DynamicFieldStyleOptionsView';
import TodaysDatePlusNumberView from './DynamicFieldTodaysDatePlusNumberView';

const getFormattedTitle = ({
  displayName,
  addDays = 0,
  type,
  editor,
  classes,
  checkboxRenderType,
  label,
  withLabel,
  onClick,
}) => {
  if (type === FormSchemaType.BOOL) {
    const { inputType } = CheckboxRenderType[checkboxRenderType] || CheckboxRenderType.Checkbox;
    const checkboxLabel = withLabel ? label || displayName : '';

    return (
      <>
        {editor.formType !== PreviewType.PDF && (
          <Box
            className="opacity-50"
            component="input"
            data-with-label={withLabel}
            sx={classes.checkbox}
            type={inputType}
            onClick={onClick}
          />
        )}
        {checkboxLabel}
      </>
    );
  }

  if (type === FormSchemaType.TODAY_X_DAYS) {
    return `Today's Date Plus ${addDays}-Days`;
  }

  return displayName;
};

const defaultAction = { args: [] };
const defaultStyle = {
  isBold: false,
  isUppercase: false,
  fontSize: 0,
  fontFamily: 0,
  fontColor: null,
  size: 100,
  float: floatOptions[0].value,
};

const DynamicFieldItemView = ({
  editor,
  isNewItem = false,
  readOnly,
  dataDynamicTitle: title = '',
  dataDynamicDateAddDays: addDays = 0,
  dataDynamicHint: hint = '',
  dataDynamicType: type = '',
  dataDynamicValue: value = '',
  dataDynamicLabel: label = '',
  dataDynamicAction: action = defaultAction,
  dataDynamicStyle: style = defaultStyle,
  dataDynamicCheckboxRenderType: checkboxRenderType = '',
  dataDynamicIgnoreUserDefinedStyles: ignoreUserDefinedStyles = false,
  dataDynamicWithLabel: withLabel = true,
  dataDynamicUsePoaVerbiage: poaVerbiage = true,
  dataDynamicPostfix: postfix,
  dataDynamicPrefix: prefix,
  dataDynamicMultiplier: multiplier,
  scale = 1,
  isLabelAlwaysVisible = false,
  ...props
}) => {
  const { isBold, isUppercase, fontSize, fontFamily, fontColor, backgroundColor, verticalAlign } =
    style;
  const ref = useRef();
  const theme = useTheme();
  const classes = getStyles(theme, {
    isBold,
    isUppercase,
    fontSize,
    fontFamily,
    fontColor,
    backgroundColor,
    verticalAlign: getPreviewVerticalAlign({ type, verticalAlign }),
  });

  const dynamicFieldSx = useMemo(
    () => ({
      ...classes.dynamicField,
      ...(isPdfFormType(editor.formType) ? classes.dynamicFieldFullFilled : {}),
      ...classes.dynamicFieldWithStyles,
    }),
    [
      classes.dynamicField,
      classes.dynamicFieldFullFilled,
      classes.dynamicFieldWithStyles,
      editor.formType,
    ],
  );

  const dispatch = useDispatch();

  const formSettingsLogo = useSelector(selectDefaultPosConfigurationLogo);
  const duplicateDynamicFieldNames = useSelector(selectDuplicateDynamicFieldNames);

  const isEdit = !!type;

  const { dynamicFieldsNamesById, dynamicFieldsSchema } = editor.config.get('dynamicFieldConfig');
  const isTagName = checkIsTagName(title);
  const isTagAssignedDate = checkIsTagAssignedDate(title);
  const isTag = isTagName || isTagAssignedDate;
  const dynamicFieldTitle = getDynamicFieldCatalogName({ dynamicFieldsNamesById, value });

  const isExcludedFromPathSuffix =
    isTag ||
    SignatureDynamicValueTypes.includes(type) ||
    type === FormSchemaType.BLANK_SIGNATURE_LINE ||
    type === FormSchemaType.IMAGE;

  const dynamicFieldTitleWithPath = useMemo(
    () =>
      isExcludedFromPathSuffix
        ? dynamicFieldTitle
        : getDynamicFieldDisplayNameWithPath({
            dynamicFieldsSchema,
            duplicateDynamicFieldNames,
            value,
            displayName: dynamicFieldTitle,
          }),
    [
      isExcludedFromPathSuffix,
      dynamicFieldsSchema,
      duplicateDynamicFieldNames,
      value,
      dynamicFieldTitle,
    ],
  );

  const displayName = isTag ? title : dynamicFieldTitleWithPath;

  const [view, setView] = useState(null);
  const [chosenOption, setChosenOption] = useState([title, value, type, label]);
  const [savedWithLabel, setSavedWithLabel] = useState(withLabel);
  const [savedPoaVerbiage, setSavedPoaVerbiage] = useState(poaVerbiage);
  const { anchorEl, onSetAnchorElement } = usePopover();

  useEffect(() => {
    if (!isNewItem) {
      return;
    }

    onSetAnchorElement(null, ref);
    setView(PopupDynamicFieldView.FIELDS_LIST_VIEW);
  }, [isNewItem, onSetAnchorElement]);

  useEffect(() => {
    const action = view ? openPopupAction : closePopupAction;
    dispatch(action());
  }, [view, dispatch]);

  const handleClose = useCallback(() => {
    if (isNewItem) {
      editor.commands.get(FormBuilderOperationType.UNDO).execute(props);
    }

    setView(null);

    editor.editing?.view.focus();
  }, [editor, isNewItem, props]);

  const onDynamicFieldItemClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      if (readOnly) {
        return;
      }

      const nextPopupView = displayName
        ? PopupDynamicFieldView.EDIT_DELETE_OPTIONS_VIEW
        : PopupDynamicFieldView.FIELDS_LIST_VIEW;

      onSetAnchorElement({ ...e });
      setView(nextPopupView);
    },
    [readOnly, displayName, onSetAnchorElement],
  );

  const onSave = useCallback(
    async ({
      value,
      type,
      style,
      checkboxRenderType,
      addDays,
      label,
      withLabel: savedWithLabel,
      poaVerbiage: savedPoaVerbiage,
      action,
      title,
      isTag,
      ignoreUserDefinedStyles,
      dataDynamicPostfix,
      dataDynamicPrefix,
      dataDynamicMultiplier,
    }) => {
      if (isTag || type === FormSchemaType.BOOL) {
        setChosenOption([title, value, type, label]);
      }

      if (savedWithLabel !== undefined || type === FormSchemaType.BOOL) {
        setSavedWithLabel(savedWithLabel);
      }

      if (!_.isUndefined(savedPoaVerbiage) || type === FormSchemaType.BLANK_SIGNATURE_LINE) {
        setSavedPoaVerbiage(savedPoaVerbiage);
      }

      const isSignatureType = SignatureDynamicValueTypes.includes(type);
      const isLogoImage = type === FormSchemaType.IMAGE && title === 'Logo';

      const logoSize = isLogoImage
        ? await getImageDimensions(formSettingsLogo, { scale, size: style.size })
        : {};

      editor.execute(FormBuilderMethodType.INSERT_REACT_COMPONENT, {
        dataTemplateFieldType: ReactRenderingContent.DYNAMIC_FIELD,
        dataDynamicType: type,
        isNewItem: false,
        dataDynamicValue: value,
        dataDynamicAction: action,
        dataDynamicStyle: { ...style, ...logoSize },
        dataDynamicDateAddDays: addDays,
        dataDynamicCheckboxRenderType: checkboxRenderType,
        dataDynamicIgnoreUserDefinedStyles: ignoreUserDefinedStyles === 'true',
        isEdit,
        ...props,
        dataDynamicLabel: label,
        dataDynamicWithLabel: savedWithLabel,
        dataDynamicUsePoaVerbiage: savedPoaVerbiage,
        dataDynamicPostfix,
        dataDynamicPrefix,
        dataDynamicMultiplier,
        ...((isTag || isLogoImage) && { dataDynamicTitle: title }),
        ...(isSignatureType && { dataDynamicTitle: label || title }),
        ...logoSize,
      });

      editor.editing?.view.focus();

      setView(null);
    },
    [editor, isEdit, props, formSettingsLogo, scale],
  );

  const chosenOptionForOptionView = useMemo(() => {
    const isTargetTagName = checkIsTagName(chosenOption[0]);
    const isTargetTagAssignedDate = checkIsTagAssignedDate(chosenOption[0]);
    const isTagNameSelected = isTagName && isTargetTagName;
    const isTagAssignedDateSelected = isTagAssignedDate && isTargetTagAssignedDate;
    const isTagSelected = !isNewItem && isTag && (isTagNameSelected || isTagAssignedDateSelected);

    return isTagSelected ? [title, value, type, label] : chosenOption;
  }, [chosenOption, isNewItem, isTag, label, title, type, value, isTagName, isTagAssignedDate]);

  const { isAvailablePostfix, isAvailablePrefix, isAvailableSpelledOut, schemaDefaultAction } =
    useMemo(() => {
      const fieldValue = chosenOptionForOptionView?.[1] || value;

      if (!dynamicFieldsSchema || !fieldValue) {
        return {
          isAvailablePostfix: false,
          isAvailablePrefix: false,
          isAvailableSpelledOut: false,
          schemaDefaultAction: undefined,
        };
      }

      const fieldsMap = flatDynamicFields(dynamicFieldsSchema);

      const field =
        fieldsMap[fieldValue] ||
        fieldsMap[fieldValue.toLowerCase()] ||
        fieldsMap[fieldValue.toUpperCase()];

      return {
        isAvailablePostfix: !!field?.isAvailablePostfix,
        isAvailablePrefix: !!field?.isAvailablePrefix,
        isAvailableSpelledOut: !!field?.isAvailableSpelledOut,
        schemaDefaultAction: field?.defaultAction,
      };
    }, [dynamicFieldsSchema, chosenOptionForOptionView, value]);

  const popupContent = useMemo(() => {
    switch (view) {
      case PopupDynamicFieldView.FIELDS_LIST_VIEW:
        return (
          <DynamicFieldItemsList
            chosenOption={chosenOption}
            dynamicFieldConfig={editor.config.get('dynamicFieldConfig')}
            setChosenOption={setChosenOption}
            setParentView={setView}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.STYLE_OPTIONS_VIEW:
        return (
          <DynamicFieldStyleOptionsView
            action={action}
            checkboxRenderType={checkboxRenderType}
            chosenOption={chosenOptionForOptionView}
            dataDynamicMultiplier={multiplier}
            dataDynamicPostfix={postfix}
            dataDynamicPrefix={prefix}
            editor={editor}
            handleClose={handleClose}
            isAvailablePostfix={isAvailablePostfix}
            isAvailablePrefix={isAvailablePrefix}
            isAvailableSpelledOut={isAvailableSpelledOut}
            isNewItem={isNewItem}
            poaVerbiage={savedPoaVerbiage}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={setView}
            style={style}
            withLabel={savedWithLabel}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.TODAYS_DATE_PLUS_NUMBER:
        return (
          <TodaysDatePlusNumberView
            action={action}
            chosenOption={chosenOption}
            editor={editor}
            handleClose={handleClose}
            isNewItem={isNewItem}
            setParentView={setView}
            style={style}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.RATE_CURRENCY_OPTIONS:
        return (
          <RateCurrencyOptions
            action={action}
            chosenOption={chosenOption}
            dataDynamicMultiplier={multiplier}
            dataDynamicPostfix={postfix}
            dataDynamicPrefix={prefix}
            editor={editor}
            handleClose={handleClose}
            isAvailablePostfix={isAvailablePostfix}
            isAvailablePrefix={isAvailablePrefix}
            isNewItem={isNewItem}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={setView}
            style={style}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.HAND_WRITING_SIGNATURE_OPTIONS:
        return (
          <DynamicFieldHandWritingSignatureOptions
            action={action}
            chosenOption={chosenOption}
            editor={editor}
            handleClose={handleClose}
            isNewItem={isNewItem}
            setParentView={setView}
            style={style}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.NOTARY_OPTIONS_VIEW:
        return (
          <DynamicFieldNotaryOptionsView
            action={action}
            chosenOption={chosenOptionForOptionView}
            editor={editor}
            handleClose={handleClose}
            isNewItem={isNewItem}
            schemaDefaultAction={schemaDefaultAction}
            setParentView={setView}
            style={style}
            onSave={onSave}
          />
        );

      case PopupDynamicFieldView.EDIT_DELETE_OPTIONS_VIEW:
        return (
          <DynamicFieldEditDeleteOptionsView
            {...props}
            dataDynamicLabel={label}
            dataDynamicTitle={title}
            displayName={displayName}
            editor={editor}
            ignoreUserDefinedStyles={ignoreUserDefinedStyles}
            setParentView={setView}
            type={type}
            value={value}
          />
        );

      default:
        return null;
    }
  }, [
    view,
    chosenOption,
    editor,
    isNewItem,
    onSave,
    action,
    checkboxRenderType,
    chosenOptionForOptionView,
    handleClose,
    style,
    savedWithLabel,
    savedPoaVerbiage,
    props,
    type,
    value,
    label,
    title,
    displayName,
    ignoreUserDefinedStyles,
    isAvailablePostfix,
    isAvailablePrefix,
    isAvailableSpelledOut,
    postfix,
    prefix,
    multiplier,
    schemaDefaultAction,
  ]);

  return (
    <Box
      component="span"
      data-testid={formsTestIds.DYNAMIC_FIELD_ITEM_VIEW}
      sx={isLabelAlwaysVisible ? classes.dynamicFieldLabelAlwaysVisible : undefined}
    >
      <Box
        component="span"
        ref={ref}
        sx={dynamicFieldSx}
        title={hint || displayName}
        onClick={onDynamicFieldItemClick}
      >
        {getFormattedTitle({
          displayName,
          addDays: addDays || action?.args?.[0],
          value,
          type,
          editor,
          checkboxRenderType,
          classes,
          label,
          withLabel: savedWithLabel,
          onClick: onDynamicFieldItemClick,
        })}
      </Box>

      {!!popupContent && (
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={defaultPopoverOptions.anchorOrigin}
          open={!!anchorEl}
          sx={classes.popover}
          transformOrigin={defaultPopoverOptions.transformOrigin}
          onClose={handleClose}
        >
          {popupContent}
        </Popover>
      )}
    </Box>
  );
};

DynamicFieldItemView.propTypes = {
  editor: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  dataDynamicAction: PropTypes.object,
  dataDynamicCheckboxRenderType: PropTypes.string,
  dataDynamicDateAddDays: PropTypes.number,
  dataDynamicHint: PropTypes.string,
  dataDynamicIgnoreUserDefinedStyles: PropTypes.bool,
  dataDynamicLabel: PropTypes.string,
  dataDynamicWithLabel: PropTypes.bool,
  dataDynamicUsePoaVerbiage: PropTypes.bool,
  dataDynamicStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dataDynamicTitle: PropTypes.string,
  dataDynamicType: PropTypes.string,
  dataDynamicValue: PropTypes.string,
  dataDynamicMultiplier: PropTypes.number,
  dataDynamicPostfix: PropTypes.string,
  dataDynamicPrefix: PropTypes.string,
  isNewItem: PropTypes.bool,
  isLabelAlwaysVisible: PropTypes.bool,
  scale: PropTypes.number,
};

export default DynamicFieldItemView;
